import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from 'antd';

const { Text } = Typography;

const MONTHS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function daysInMonth(month: number, year: number): number {
    if (!month || !year) return 31;
    return new Date(year, month, 0).getDate();
}

interface DatePickerHybridProps {
    value?: string; // ISO 8601 YYYY-MM-DD
    onChange?: (value: string | undefined) => void;
    primaryColor?: string;
}

const DatePickerHybrid: React.FC<DatePickerHybridProps> = ({
    value,
    onChange,
    primaryColor = '#2D9CC9',
}) => {
    const currentYear = new Date().getFullYear();

    const parseValue = (iso?: string) => {
        if (!iso) return { day: '', month: '', year: '' };
        const parts = iso.split('-');
        return {
            year: parts[0] ?? '',
            month: parts[1] ? String(parseInt(parts[1], 10)) : '',
            day: parts[2] ? String(parseInt(parts[2], 10)) : '',
        };
    };

    const initial = parseValue(value);
    const [day, setDay] = useState<string>(initial.day);
    const [month, setMonth] = useState<string>(initial.month);
    const [year, setYear] = useState<string>(initial.year);
    const [error, setError] = useState<string>('');

    // Sync if value changes from outside
    useEffect(() => {
        const parsed = parseValue(value);
        setDay(parsed.day);
        setMonth(parsed.month);
        setYear(parsed.year);
    }, [value]);

    const validate = useCallback(
        (d: string, m: string, y: string): string => {
            if (!d && !m && !y) return 'Veuillez saisir votre date de naissance';
            if (!d || !m || !y) return '';
            const dayN = parseInt(d, 10);
            const monthN = parseInt(m, 10);
            const yearN = parseInt(y, 10);
            const maxDay = daysInMonth(monthN, yearN);
            if (dayN > maxDay) return `Le mois ${MONTHS[monthN - 1]} n'a que ${maxDay} jours`;
            const date = new Date(yearN, monthN - 1, dayN);
            if (date > new Date()) return 'La date doit être dans le passé';
            return '';
        },
        []
    );

    const emit = useCallback(
        (d: string, m: string, y: string) => {
            const err = validate(d, m, y);
            setError(err);
            if (d && m && y && !err) {
                const iso = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
                onChange?.(iso);
            } else {
                onChange?.(undefined);
            }
        },
        [validate, onChange]
    );

    const handleDay = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setDay(e.target.value);
        emit(e.target.value, month, year);
    };
    const handleMonth = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setMonth(e.target.value);
        emit(day, e.target.value, year);
    };
    const handleYear = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setYear(e.target.value);
        emit(day, month, e.target.value);
    };

    const selectStyle: React.CSSProperties = {
        height: 48,
        minHeight: 44,
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '0 8px',
        fontSize: 15,
        background: '#f8fafc',
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        outline: 'none',
        appearance: 'auto' as any,
    };

    const focusStyle = (focused: boolean): React.CSSProperties =>
        focused
            ? { borderColor: primaryColor, boxShadow: `0 0 0 2px ${primaryColor}33` }
            : {};

    const [focusedField, setFocusedField] = useState<'day' | 'month' | 'year' | null>(null);

    const maxDay = month && year ? daysInMonth(parseInt(month, 10), parseInt(year, 10)) : 31;
    const days = Array.from({ length: maxDay }, (_, i) => i + 1);
    const years = Array.from({ length: 71 }, (_, i) => currentYear - 10 - i);

    return (
        <div>
            <Text
                style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#0f172a',
                    marginBottom: 8,
                }}
            >
                Date de naissance
            </Text>
            <div
                style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                }}
            >
                {/* Jour */}
                <select
                    value={day}
                    onChange={handleDay}
                    aria-label="Jour de naissance"
                    onFocus={() => setFocusedField('day')}
                    onBlur={() => setFocusedField(null)}
                    style={{
                        ...selectStyle,
                        flex: '1 1 80px',
                        minWidth: 80,
                        ...focusStyle(focusedField === 'day'),
                    }}
                >
                    <option value="">Jour</option>
                    {days.map((d) => (
                        <option key={d} value={String(d)}>
                            {String(d).padStart(2, '0')}
                        </option>
                    ))}
                </select>

                {/* Mois */}
                <select
                    value={month}
                    onChange={handleMonth}
                    aria-label="Mois de naissance"
                    onFocus={() => setFocusedField('month')}
                    onBlur={() => setFocusedField(null)}
                    style={{
                        ...selectStyle,
                        flex: '2 1 130px',
                        minWidth: 130,
                        ...focusStyle(focusedField === 'month'),
                    }}
                >
                    <option value="">Mois</option>
                    {MONTHS.map((m, i) => (
                        <option key={i + 1} value={String(i + 1)}>
                            {m}
                        </option>
                    ))}
                </select>

                {/* Année */}
                <select
                    value={year}
                    onChange={handleYear}
                    aria-label="Année de naissance"
                    onFocus={() => setFocusedField('year')}
                    onBlur={() => setFocusedField(null)}
                    style={{
                        ...selectStyle,
                        flex: '1 1 100px',
                        minWidth: 100,
                        ...focusStyle(focusedField === 'year'),
                    }}
                >
                    <option value="">Année</option>
                    {years.map((y) => (
                        <option key={y} value={String(y)}>
                            {y}
                        </option>
                    ))}
                </select>
            </div>

            {error && (
                <Text
                    style={{
                        display: 'block',
                        marginTop: 4,
                        fontSize: 13,
                        color: '#ff4d4f',
                    }}
                >
                    {error}
                </Text>
            )}
        </div>
    );
};

export default DatePickerHybrid;
